/**
 * 多言語放送 (Issue #449) で選択可能な Amazon Polly の言語 / VoiceId 定義。
 *
 * - システムは Polly を `Engine=neural` 前提で使用するため、Neural 対応の VoiceId のみを
 *   列挙する。Standard 専用 (例: `Mizuki`) は Neural では合成できず
 *   `ValidationException` になるため意図的に除外している。
 * - `BroadcastPointForm` の言語 / 音声セレクトと、`BroadcastContentsEditor` の音声プレビュー
 *   (POST /audio-preview の `voice_id`) の両方で共有する。
 *
 * ⚠️ このファイルは **手で編集しないこと**。
 *   言語 / 話者の唯一の正源は `lambda/common/polly_voices.py` で、本ファイルは
 *   `python3 scripts/gen_polly_voices_ts.py --write` で機械生成する。
 *   乖離は `tests/polly_voices_sync/test_polly_voices_sync.py` が CI で検知する。
 *   (従来は 4 箇所を手で同期するルールだったが、言語が増えるほど
 *    同期漏れが避けられない構造だったため生成方式に切り替えた)
 */

/** 1 件ぶんの Polly VoiceId 選択肢 */
export interface PollyVoiceOption {
  /** Polly の VoiceId (例: `"Takumi"`)。API / DB にはこの値がそのまま保存される。 */
  readonly id: string;
  /** UI 表示用ラベル (例: `"Takumi（男性・日本語 Neural）"`)。 */
  readonly label: string;
}

/** 1 言語ぶんの定義 (language_key + Polly LanguageCode + 話者一覧) */
export interface PollyLanguageOption {
  /** 放送設定に保存する短い言語キー (例: `"ja"`)。 */
  readonly key: string;
  /** Polly の `LanguageCode` (例: `"ja-JP"` / `"cmn-CN"`)。 */
  readonly languageCode: string;
  /** 管理画面の表示名 (例: `"日本語"`)。 */
  readonly label: string;
  /** 当該言語で選択できる話者一覧。**先頭が既定話者**。 */
  readonly voices: readonly PollyVoiceOption[];
}

/**
 * 選択可能な言語の一覧 (マスタ定義順)。
 *
 * この順序が **放送順序** でもある (受け入れ条件④: 言語ごとに全コンテンツを
 * まとめて流す順序)。チェックボックスの表示順もこの順に揃えること。
 */
export const POLLY_LANGUAGE_OPTIONS: readonly PollyLanguageOption[] = [
  {
    key: 'ja',
    languageCode: 'ja-JP',
    label: '日本語',
    voices: [
      { id: 'Takumi', label: 'Takumi（男性・日本語 Neural）' },
      { id: 'Kazuha', label: 'Kazuha（女性・日本語 Neural）' },
      { id: 'Tomoko', label: 'Tomoko（女性・日本語 Neural）' },
    ],
  },
  {
    key: 'en',
    languageCode: 'en-US',
    label: '英語（米国）',
    voices: [
      { id: 'Joanna', label: 'Joanna（女性・英語 米国 Neural）' },
      { id: 'Matthew', label: 'Matthew（男性・英語 米国 Neural）' },
      { id: 'Ivy', label: 'Ivy（女性・英語 米国 Neural）' },
      { id: 'Kendra', label: 'Kendra（女性・英語 米国 Neural）' },
      { id: 'Kimberly', label: 'Kimberly（女性・英語 米国 Neural）' },
      { id: 'Salli', label: 'Salli（女性・英語 米国 Neural）' },
      { id: 'Joey', label: 'Joey（男性・英語 米国 Neural）' },
      { id: 'Stephen', label: 'Stephen（男性・英語 米国 Neural）' },
      { id: 'Ruth', label: 'Ruth（女性・英語 米国 Neural）' },
    ],
  },
  {
    key: 'zh',
    languageCode: 'cmn-CN',
    label: '中国語（標準）',
    voices: [{ id: 'Zhiyu', label: 'Zhiyu（女性・中国語 標準 Neural）' }],
  },
  {
    key: 'ko',
    languageCode: 'ko-KR',
    label: '韓国語',
    voices: [{ id: 'Seoyeon', label: 'Seoyeon（女性・韓国語 Neural）' }],
  },
  {
    key: 'en-GB',
    languageCode: 'en-GB',
    label: '英語（英国）',
    voices: [
      { id: 'Amy', label: 'Amy（女性・英語 英国 Neural）' },
      { id: 'Emma', label: 'Emma（女性・英語 英国 Neural）' },
      { id: 'Brian', label: 'Brian（男性・英語 英国 Neural）' },
      { id: 'Arthur', label: 'Arthur（男性・英語 英国 Neural）' },
    ],
  },
  {
    key: 'en-AU',
    languageCode: 'en-AU',
    label: '英語（オーストラリア）',
    voices: [{ id: 'Olivia', label: 'Olivia（女性・英語 豪州 Neural）' }],
  },
  {
    key: 'fr',
    languageCode: 'fr-FR',
    label: 'フランス語',
    voices: [
      { id: 'Lea', label: 'Lea（女性・仏語 Neural）' },
      { id: 'Remi', label: 'Remi（男性・仏語 Neural）' },
    ],
  },
  {
    key: 'fr-CA',
    languageCode: 'fr-CA',
    label: 'フランス語（カナダ）',
    voices: [
      { id: 'Gabrielle', label: 'Gabrielle（女性・仏語 カナダ Neural）' },
      { id: 'Liam', label: 'Liam（男性・仏語 カナダ Neural）' },
    ],
  },
  {
    key: 'de',
    languageCode: 'de-DE',
    label: 'ドイツ語',
    voices: [
      { id: 'Vicki', label: 'Vicki（女性・独語 Neural）' },
      { id: 'Daniel', label: 'Daniel（男性・独語 Neural）' },
    ],
  },
  {
    key: 'es',
    languageCode: 'es-ES',
    label: 'スペイン語',
    voices: [
      { id: 'Lucia', label: 'Lucia（女性・西語 Neural）' },
      { id: 'Sergio', label: 'Sergio（男性・西語 Neural）' },
    ],
  },
  {
    key: 'es-MX',
    languageCode: 'es-MX',
    label: 'スペイン語（メキシコ）',
    voices: [{ id: 'Mia', label: 'Mia（女性・西語 メキシコ Neural）' }],
  },
  {
    key: 'pt-BR',
    languageCode: 'pt-BR',
    label: 'ポルトガル語（ブラジル）',
    voices: [
      { id: 'Camila', label: 'Camila（女性・葡語 ブラジル Neural）' },
      { id: 'Vitoria', label: 'Vitoria（女性・葡語 ブラジル Neural）' },
      { id: 'Thiago', label: 'Thiago（男性・葡語 ブラジル Neural）' },
    ],
  },
  {
    key: 'it',
    languageCode: 'it-IT',
    label: 'イタリア語',
    voices: [
      { id: 'Bianca', label: 'Bianca（女性・伊語 Neural）' },
      { id: 'Adriano', label: 'Adriano（男性・伊語 Neural）' },
    ],
  },
  {
    key: 'nl',
    languageCode: 'nl-NL',
    label: 'オランダ語',
    voices: [{ id: 'Laura', label: 'Laura（女性・蘭語 Neural）' }],
  },
  {
    key: 'hi',
    languageCode: 'hi-IN',
    label: 'ヒンディー語',
    voices: [{ id: 'Kajal', label: 'Kajal（女性・ヒンディー語 Neural）' }],
  },
  {
    key: 'ar',
    languageCode: 'arb',
    label: 'アラビア語',
    voices: [{ id: 'Hala', label: 'Hala（女性・アラビア語 Neural）' }],
  },
  {
    key: 'pl',
    languageCode: 'pl-PL',
    label: 'ポーランド語',
    voices: [{ id: 'Ola', label: 'Ola（女性・波語 Neural）' }],
  },
  {
    key: 'sv',
    languageCode: 'sv-SE',
    label: 'スウェーデン語',
    voices: [{ id: 'Elin', label: 'Elin（女性・瑞語 Neural）' }],
  },
  {
    key: 'da',
    languageCode: 'da-DK',
    label: 'デンマーク語',
    voices: [{ id: 'Sofie', label: 'Sofie（女性・丁語 Neural）' }],
  },
  {
    key: 'nb',
    languageCode: 'nb-NO',
    label: 'ノルウェー語',
    voices: [{ id: 'Ida', label: 'Ida（女性・諾語 Neural）' }],
  },
  {
    key: 'fi',
    languageCode: 'fi-FI',
    label: 'フィンランド語',
    voices: [{ id: 'Suvi', label: 'Suvi（女性・芬語 Neural）' }],
  },
];

/**
 * 基本 4 言語 (日 / 英 / 中 / 韓)。
 *
 * 自列車遅延 / 接続線遅延を含む **全コンテンツ** をこの 4 言語で放送できる
 * (受け入れ条件「自列車遅延／接続線遅延は日本語・英語・中国語・韓国語で放送されること」)。
 * 任意テキストの放送はこれに加えてその他の言語も選択できる。
 */
export const BASE_LANGUAGE_KEYS: readonly string[] = ['ja', 'en', 'zh', 'ko'];

/** `languages` 未指定時の既定 (= 従来どおり日本語のみ放送)。 */
export const DEFAULT_LANGUAGE_KEY = 'ja';

/**
 * 1 放送地点 / 1 コンテンツに指定できる言語数の上限。
 *
 * 言語を増やすほど Polly 合成回数と放送尺が線形に増えるため上限を設ける。
 * バックエンド (`lambda/control-panel-api/index.py` の `MAX_SPOT_LANGUAGES`) と
 * OpenAPI (`languages.maxItems`) と一致させること。
 */
export const MAX_SPOT_LANGUAGES = 8;

/**
 * 全言語ぶんの VoiceId 選択肢 (フラット)。
 *
 * 音声プレビュー (`POST /audio-preview`) のように「言語を問わず話者を選ぶ」
 * 箇所で使う。言語別に絞り込みたい場合は `voiceOptionsForLanguage()` を使うこと。
 */
export const POLLY_VOICE_OPTIONS: readonly PollyVoiceOption[] = POLLY_LANGUAGE_OPTIONS.flatMap(
  (lang) => lang.voices,
);

/**
 * `voice_id` 未指定時に使用するシステムデフォルト。
 * 現行の実放送・プレビューが従来から `Takumi` 固定だったため、後方互換のため同値。
 */
export const DEFAULT_POLLY_VOICE_ID = 'Takumi';

/** `POLLY_VOICE_OPTIONS` の id のみを取り出した配列 (バリデーション用) */
export const POLLY_VOICE_IDS: readonly string[] = POLLY_VOICE_OPTIONS.map((o) => o.id);

/** `POLLY_LANGUAGE_OPTIONS` の key のみを取り出した配列 (バリデーション用) */
export const POLLY_LANGUAGE_KEYS: readonly string[] = POLLY_LANGUAGE_OPTIONS.map((l) => l.key);

/**
 * 与えられた voice_id が有効か (許容リストに含まれるか) を判定する。
 * 空文字 / undefined / 未登録の値はすべて false を返す。
 */
export function isValidPollyVoiceId(value: string | undefined | null): value is string {
  if (typeof value !== 'string' || value === '') return false;
  return POLLY_VOICE_IDS.includes(value);
}

/**
 * 与えられた language_key が有効か (マスタに存在するか) を判定する。
 */
export function isValidLanguageKey(value: string | undefined | null): value is string {
  if (typeof value !== 'string' || value === '') return false;
  return POLLY_LANGUAGE_KEYS.includes(value);
}

/** `language_key` に対応する言語定義を返す (未登録は undefined)。 */
export function findPollyLanguage(
  languageKey: string | undefined | null,
): PollyLanguageOption | undefined {
  if (typeof languageKey !== 'string' || languageKey === '') return undefined;
  return POLLY_LANGUAGE_OPTIONS.find((lang) => lang.key === languageKey);
}

/**
 * `language_key` で選択できる話者一覧を返す (先頭が既定話者)。
 * 未登録の言語では空配列を返す。
 */
export function voiceOptionsForLanguage(
  languageKey: string | undefined | null,
): readonly PollyVoiceOption[] {
  return findPollyLanguage(languageKey)?.voices ?? [];
}

/**
 * `language_key` の既定話者 (VoiceId) を返す。
 *
 * 未登録の言語ではシステムデフォルト (`Takumi`) にフォールバックする
 * (サーバ側 `resolve_default_voice_id()` と同じ挙動)。
 */
export function defaultVoiceIdForLanguage(languageKey: string | undefined | null): string {
  return voiceOptionsForLanguage(languageKey)[0]?.id ?? DEFAULT_POLLY_VOICE_ID;
}

/**
 * `voice_id` が `language_key` で利用可能か判定する。
 *
 * 「英語の放送地点に日本語話者 (Takumi) を設定してしまう」設定ミスを保存前に
 * 検知するために使う (サーバ側も同じ検証を行い、不整合は 400 で拒否する)。
 */
export function isVoiceIdValidForLanguage(
  languageKey: string | undefined | null,
  voiceId: string | undefined | null,
): boolean {
  if (typeof voiceId !== 'string' || voiceId === '') return false;
  return voiceOptionsForLanguage(languageKey).some((v) => v.id === voiceId);
}

/**
 * `languages` を **マスタ定義順** に正規化する (重複排除 + 未登録値の除外)。
 *
 * 放送は「言語ごとに全コンテンツをまとめて流す」順序で行うため、UI 上の
 * チェック順で保存すると放送順序が地点ごとにブレてしまう。サーバ側
 * (`normalize_language_keys()`) と同じ規則でフロントでも正規化しておくことで、
 * 保存前のプレビュー表示と実際の放送順序を一致させられる。
 */
export function normalizeLanguageKeys(values: readonly string[] | undefined | null): string[] {
  if (!Array.isArray(values)) return [];
  const selected = new Set(values.filter((v) => isValidLanguageKey(v)));
  return POLLY_LANGUAGE_KEYS.filter((key) => selected.has(key));
}
